import { z } from 'zod';
import { JobErrorSchema, JobExecutionSchema } from './Job';
import { ConnectionLineType } from './Simulation';
import { ComponentInstanceSchema } from '../types/DiagramComponentInstance.schema';
import { DiagramConnectionSchema } from '../types/DiagramConnection.schema';
import { VoltageType } from '../types/VoltageType';

export enum Strictness {
    STRICT = 'STRICT',
    LESS_STRICT = 'LESS_STRICT',
    EVEN_LESS_STRICT = 'EVEN_LESS_STRICT',
}

export const GetScenariosResponseSchema = z.object({
    success: z.boolean().optional(),
    diagram: z
        .object({
            componentInstances: z.record(ComponentInstanceSchema.shape.id, z.unknown()),
            connections: z.record(DiagramConnectionSchema.shape.id, z.unknown()),
        })
        .optional(),
    csv: z.string().optional(),
    error: z
        .object({
            message: z.string(),
            type: z.string().optional(),
            stackTrace: z.string().array().optional(),
        })
        .optional(),
});

export const ConnectionLineDataSchema = z.object({
    current: z.number(),
});

export const ConnectionLinesSchema = z.record(z.nativeEnum(ConnectionLineType), ConnectionLineDataSchema);

export const ConnectionDataSchema = z.object({
    lines: ConnectionLinesSchema.optional(),
    voltage: z.number().optional(),
    updatedAt: z.string().datetime().optional(),
    status: z.enum(['pending', 'done', 'error']).optional(),
    errors: z.array(z.string()).optional(),
});

export const WorstCaseSimulationResultSchema = z.object({
    connections: z.record(DiagramConnectionSchema.shape.id, ConnectionDataSchema),
});

export const StatusMessageSchema = z.object({
    message: z.string(),
    timestamp: z.string().datetime(),
});

export const WorstCaseSimulationJobDataSchema = z.object({
    componentInstances: z.record(ComponentInstanceSchema.shape.id, z.unknown()),
    connections: z.record(DiagramConnectionSchema.shape.id, z.unknown()),
});

export const ErrorSchema = z.preprocess(
    (input) => {
        return typeof input === 'string'
            ? {
                  message: input,
              }
            : input;
    },
    z.object({
        message: z.string(),
        connectionIds: z.array(DiagramConnectionSchema.shape.id).optional(),
    }),
);

export const WorstCaseZoneSchema = z.object({
    hash: z.string(),
    componentInstances: z.record(ComponentInstanceSchema.shape.id, z.unknown()),
    connections: z.record(DiagramConnectionSchema.shape.id, z.unknown()),
    endpoints: z.array(z.string()),
    voltage: z.object({
        min: z.number().nullable(),
        max: z.number().nullable(),
    }),
    voltageType: VoltageType.validator,
    errors: z.array(ErrorSchema).optional(),
});

export const GetZonesResponseSchema = z.object({
    zones: z.array(WorstCaseZoneSchema),
});

export const WorstCaseSimulationDataSchema = z.object({
    componentInstances: z.record(ComponentInstanceSchema.shape.id, z.unknown()),
    connections: z.record(DiagramConnectionSchema.shape.id, z.unknown()),
    endpoints: z.array(z.string()),
    voltage: z.object({
        min: z.number().nullable(),
        max: z.number().nullable(),
    }),
    voltageType: VoltageType.validator,
});

export const CallbackRequestSchema = z.object({
    status: z.string().optional(),
    statusMessage: z.string().optional(),
    execution: JobExecutionSchema.optional(),
    error: JobErrorSchema.optional(),
    result: z
        .object({
            connections: z.record(
                DiagramConnectionSchema.shape.id,
                z.object({
                    lines: z.record(
                        z.nativeEnum(ConnectionLineType),
                        z.object({
                            current: z.union([z.number(), z.array(z.number())]),
                        }),
                    ),
                }),
            ),
        })
        .optional(),
});

export type WorstCaseSimulationResult = z.infer<typeof WorstCaseSimulationResultSchema>;
export type WorstCaseSimulationJobData = z.infer<typeof WorstCaseSimulationJobDataSchema>;
export type WorstCaseZone = z.infer<typeof WorstCaseZoneSchema>;
export type WorstCaseSimulationData = z.infer<typeof WorstCaseSimulationDataSchema>;
export type CallbackRequest = z.infer<typeof CallbackRequestSchema>;
export type StatusMessage = z.infer<typeof StatusMessageSchema>;
export type Error = z.infer<typeof ErrorSchema>;
export type GetZonesResponse = z.infer<typeof GetZonesResponseSchema>;
export type GetScenariosResponse = z.infer<typeof GetScenariosResponseSchema>;
